/**
 * Paginated reads for queries that must see every row.
 *
 * PostgREST caps an unbounded `select()` at 1,000 rows and returns the truncated
 * page with a 200 and no warning. A dedupe or a load tally built on that page is
 * silently wrong for every row past the cap: the lead inbox import shipped with
 * this bug and stopped suppressing leads that were already contacts once the
 * table passed 1,000 rows — position 1,477 of 1,513 came through as new.
 *
 * Any read whose result feeds a correctness decision, rather than a page of UI,
 * goes through one of the helpers here.
 *
 * Canonical home: this is pure, dependency-free pagination logic shared by both
 * the browser bundle (client hooks under src/hooks/, via Supabase RLS-scoped
 * reads) and Vercel serverless functions (api/_lib/, via the service client) —
 * it lives under src/lib/ so client code never has to import from api/_lib/,
 * whose module graph is Vercel Function-only and is not servable to the
 * browser by the dev server. api/_lib/read-all-rows.ts re-exports this file;
 * it does not duplicate it.
 */

/** PostgREST's default `max-rows`. A full page means there is probably another. */
export const SUPABASE_READ_PAGE_SIZE = 1000;

type KeysetCursor = string | number;

interface ReadPageResult {
  data?: unknown;
  error: unknown;
}

interface KeysetPageQuery<TCursor extends KeysetCursor> {
  gt: (column: string, value: TCursor) => KeysetPageQuery<TCursor>;
  order: (
    column: string,
    options: { ascending: true },
  ) => {
    limit: (count: number) => PromiseLike<ReadPageResult>;
  };
}

/**
 * Reads every row a query matches, one page at a time.
 *
 * `readPage` must apply a deterministic order — pagination over an unordered
 * result can skip or repeat rows between pages, which would reintroduce the
 * same class of silent miss this helper exists to remove.
 *
 * New callers that require the complete matching SET should use
 * `readAllRowsKeyset`, because an offset can shift under concurrent inserts or
 * deletes even when the order itself is deterministic. Keep this offset helper
 * for reads whose result does not require set-completeness and whose order key
 * is immutable.
 *
 * @param readPage  builds and runs one page, inclusive of both bounds
 * @param onError   called with the PostgREST error; must throw
 */
export async function readAllRows<T>(
  readPage: (from: number, to: number) => PromiseLike<ReadPageResult>,
  onError: (error: unknown) => never,
  pageSize: number = SUPABASE_READ_PAGE_SIZE,
): Promise<T[]> {
  const rows: T[] = [];

  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await readPage(offset, offset + pageSize - 1);
    if (error) onError(error);

    const page = (data ?? []) as T[];
    rows.push(...page);

    if (page.length < pageSize) return rows;
  }
}

/**
 * Reads the complete matching row set with a cursor anchored to the last row
 * seen. The cursor column must be unique and immutable.
 *
 * The helper owns the cursor predicate and ordering: every continuation uses
 * `gt(cursorColumn, cursor)`, every page is ordered by that column ascending,
 * and callers restore any display order in memory after this resolves.
 *
 * @param buildPage     builds the tenant/filter-scoped query for one page
 * @param cursorColumn  unique, immutable column used as the keyset cursor
 * @param getCursor     extracts that column's value from a returned row
 * @param onError       called with the PostgREST error; must throw
 */
export async function readAllRowsKeyset<T, TCursor extends KeysetCursor>(
  buildPage: (cursor: TCursor | null) => KeysetPageQuery<TCursor>,
  cursorColumn: string,
  getCursor: (row: T) => TCursor,
  onError: (error: unknown) => never,
  pageSize: number = SUPABASE_READ_PAGE_SIZE,
): Promise<T[]> {
  const rows: T[] = [];
  const seenIds = new Set<TCursor>();
  let cursor: TCursor | null = null;

  for (;;) {
    let pageQuery = buildPage(cursor);
    if (cursor !== null) {
      pageQuery = pageQuery.gt(cursorColumn, cursor);
    }

    const { data, error } = await pageQuery
      .order(cursorColumn, { ascending: true })
      .limit(pageSize);
    if (error) onError(error);

    const page = (data ?? []) as T[];
    for (const row of page) {
      const id = getCursor(row);
      // Defence in depth only: id dedupe is NOT a substitute for the cursor.
      // Dedupe cannot restore a row that an offset-shifted read never saw.
      if (seenIds.has(id)) continue;
      seenIds.add(id);
      rows.push(row);
    }

    if (page.length < pageSize) return rows;

    const nextCursor = getCursor(page[page.length - 1] as T);
    if (cursor !== null && Object.is(nextCursor, cursor)) {
      throw new Error(`Keyset cursor column ${cursorColumn} did not advance`);
    }
    cursor = nextCursor;
  }
}
